import { ClassNameValue } from 'tailwind-merge'

import { useGetUserById } from '@/api/get-user-by-id.hook'

import { LEMA_USER_ID } from '@/const/contact'

import { getUserInitials } from '@/lib/string/string-utils'
import { cn } from '@/lib/ui/style-helpers'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/lemmala-design-system/avatar'
import { getColorForName } from '@/components/lemmala-design-system/avatar/utils'
import { Tooltip } from '@/components/lemmala-design-system/tooltip/tooltip'

type UserAvatarProps = {
  userId: string
  showEmail?: boolean
  className?: ClassNameValue
  withTooltip?: boolean
  // Rendered as initials when the user id can't be resolved (e.g. a deleted
  // user 404s), so the avatar degrades to a placeholder instead of vanishing.
  fallbackName?: string
}

export const UserAvatar = ({
  userId,
  showEmail,
  className,
  withTooltip = true,
  fallbackName,
}: UserAvatarProps) => {
  const { user, isError } = useGetUserById(userId)
  if (isError && !fallbackName) return null
  const name = user?.name ?? fallbackName
  const userInitials = getUserInitials(name ?? '')
  // The Lema mark is a square logo, not a person: render it without the
  // circular chip background and inset so it isn't clipped by the crop.
  const isLemaMark = userId === LEMA_USER_ID
  const avatar = (
    <Avatar
      color={isLemaMark || !name ? undefined : getColorForName(name)}
      className={cn('size-6 text-xs', isLemaMark && 'bg-transparent', className)}
    >
      <AvatarImage
        src={user?.avatarUrl}
        alt={name}
        title={name}
        className={cn(isLemaMark && 'object-contain p-0.5')}
      />
      <AvatarFallback>{userInitials}</AvatarFallback>
    </Avatar>
  )
  if (!withTooltip) return avatar

  return (
    <div className={'flex items-center gap-2'}>
      <Tooltip trigger={avatar}>
        <div className={cn('flex flex-col items-center gap-1', showEmail && 'px-3 py-1.5')}>
          <span className={cn('text-main-foreground-primary', showEmail && 'font-semibold')}>
            {user?.name}
          </span>
          {showEmail && <span className='text-main-foreground-secondary'>{user?.email}</span>}
        </div>
      </Tooltip>
    </div>
  )
}
